import { isEntityRelation } from 'src/constants/beneficiary-relationship';

import type { DependentTargetOption, HouseholdContactFormValues } from './types';
import { DEPENDENT_TARGET_NONE, DEPENDENT_TARGET_PREFIX, DEPENDENT_TARGET_UNSET } from './constants';

export type DependentTargetClient = {
  userId: string;
  firstName: string;
  lastName: string;
};

function fullName({ firstName, lastName }: { firstName: string; lastName: string }) {
  return `${firstName} ${lastName}`.trim();
}

/**
 * The household option is labelled with both clients because that is what an advisor recognises —
 * "Dependent of the household" means nothing to them. With one client there is no combined option:
 * it would be the same person twice. Empty with no clients, which hides the picker entirely.
 */
export function buildDependentTargetOptions({
  householdId,
  clients,
}: {
  householdId: string;
  clients: readonly DependentTargetClient[];
}): DependentTargetOption[] {
  if (clients.length === 0) return [];

  const noneOption = { value: DEPENDENT_TARGET_NONE, label: 'No specific person' };
  const clientOptions = clients.map((client) => ({
    value: `${DEPENDENT_TARGET_PREFIX.user}${client.userId}`,
    label: fullName(client),
  }));

  if (clients.length === 1) return [...clientOptions, noneOption];

  const sharedLastName = clients.every((client) => client.lastName === clients[0]?.lastName);
  const combinedLabel = sharedLastName
    ? `${clients.map((client) => client.firstName).join(' & ')} ${clients[0]?.lastName ?? ''}`.trim()
    : clients.map(fullName).join(' & ');

  return [
    { value: `${DEPENDENT_TARGET_PREFIX.household}${householdId}`, label: combinedLabel },
    ...clientOptions,
    noneOption,
  ];
}

function isPresent(value: string | null | undefined): value is string {
  return value !== null && value !== undefined && value !== '';
}

type HouseholdClientProfile =
  | { user: { id: string; firstName?: string | null; lastName?: string | null } }
  | null
  | undefined;

/** Primary plus co-client — the same set the mutation validates a dependent target against. */
export function toDependentTargetClients(
  primaryClient: HouseholdClientProfile,
  coClient: HouseholdClientProfile,
): DependentTargetClient[] {
  return [primaryClient, coClient].flatMap((client) =>
    client === null || client === undefined
      ? []
      : [{ userId: client.user.id, firstName: client.user.firstName ?? '', lastName: client.user.lastName ?? '' }],
  );
}

export function encodeDependentTarget({
  dependentOfHouseholdId,
  dependentOfUserId,
}: {
  dependentOfHouseholdId?: string | null;
  dependentOfUserId?: string | null;
}): string {
  if (isPresent(dependentOfHouseholdId)) {
    return `${DEPENDENT_TARGET_PREFIX.household}${dependentOfHouseholdId}`;
  }
  if (isPresent(dependentOfUserId)) {
    return `${DEPENDENT_TARGET_PREFIX.user}${dependentOfUserId}`;
  }
  return DEPENDENT_TARGET_NONE;
}

/**
 * Always returns both keys. The mutation reads a missing key as "keep what is on the record", so
 * omitting the unused one would strand the previous target instead of clearing it.
 */
export function decodeDependentTarget(dependentOf: string): {
  dependentOfHouseholdId: string | null;
  dependentOfUserId: string | null;
} {
  if (dependentOf.startsWith(DEPENDENT_TARGET_PREFIX.household)) {
    return {
      dependentOfHouseholdId: dependentOf.slice(DEPENDENT_TARGET_PREFIX.household.length),
      dependentOfUserId: null,
    };
  }
  if (dependentOf.startsWith(DEPENDENT_TARGET_PREFIX.user)) {
    return {
      dependentOfHouseholdId: null,
      dependentOfUserId: dependentOf.slice(DEPENDENT_TARGET_PREFIX.user.length),
    };
  }
  return { dependentOfHouseholdId: null, dependentOfUserId: null };
}

export function isEntityForm(values: { relation?: string }) {
  return isEntityRelation(values.relation ?? '');
}

/**
 * An entity contact is named by its entity name; the person name fields do not apply to it. Takes a
 * partial because it reads react-hook-form's live watch, which types every field as optional.
 */
export function canSaveHouseholdContact(values: Partial<HouseholdContactFormValues>) {
  if (!isFilled(values.relation)) return false;
  if (isEntityForm(values)) return isFilled(values.entityName);
  return isFilled(values.firstName) && isFilled(values.lastName);
}

function isFilled(value: string | undefined) {
  return (value ?? '').trim() !== '';
}

function emptyToNull(value: string) {
  return value === '' ? null : value;
}

function orEmpty(value: string | null | undefined) {
  return value ?? '';
}

type ExistingContact = {
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  relation?: string | null;
  isBeneficiary?: boolean | null;
  isDependent?: boolean | null;
  isTrustedContact?: boolean | null;
  dependentOfHouseholdId?: string | null;
  dependentOfUserId?: string | null;
  dateOfBirth?: string | null;
  ssn?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
  notes?: string | null;
  entityType?: string | null;
  entityName?: string | null;
  entityEin?: string | null;
  entityDateEstablished?: string | null;
  entityDateOfDeath?: string | null;
  entityTrustType?: string | null;
  entityOrganizationType?: string | null;
  entityTrusteeName?: string | null;
  entityPrimaryContactRole?: string | null;
  entityExecutorName?: string | null;
};

export function toHouseholdContactFormValues(contact: ExistingContact): HouseholdContactFormValues {
  const relation = orEmpty(contact.relation);
  const isEntity = isEntityRelation(relation);

  return {
    // An entity's name is stored on the contact's first name, and the form edits it as entityName.
    firstName: isEntity ? '' : orEmpty(contact.firstName),
    middleName: orEmpty(contact.middleName),
    lastName: orEmpty(contact.lastName),
    relation,
    isBeneficiary: contact.isBeneficiary === true,
    isDependent: contact.isDependent === true,
    isTrustedContact: contact.isTrustedContact === true,
    // A contact who is not a dependent has no answer to echo, so the picker opens on its placeholder
    // the first time Dependent is ticked. Only a stored dependent keeps "No specific person".
    dependentOf: contact.isDependent === true ? encodeDependentTarget(contact) : DEPENDENT_TARGET_UNSET,
    // Only an entity falls back to first name — that is where the mutation stores an entity's name.
    // A person's first name here would surface as a pre-filled Entity name on switching relation.
    entityName:
      isEntity && orEmpty(contact.entityName) === '' ? orEmpty(contact.firstName) : orEmpty(contact.entityName),
    entityEin: orEmpty(contact.entityEin),
    entityDateEstablished: orEmpty(contact.entityDateEstablished),
    entityTrustType: orEmpty(contact.entityTrustType),
    entityTrusteeName: orEmpty(contact.entityTrusteeName),
    entityOrganizationType: orEmpty(contact.entityOrganizationType),
    entityPrimaryContactRole: orEmpty(contact.entityPrimaryContactRole),
    entityDateOfDeath: orEmpty(contact.entityDateOfDeath),
    entityExecutorName: orEmpty(contact.entityExecutorName),
    dateOfBirth: orEmpty(contact.dateOfBirth),
    ssn: orEmpty(contact.ssn),
    phoneNumber: orEmpty(contact.phoneNumber),
    email: orEmpty(contact.email),
    addressLine1: orEmpty(contact.addressLine1),
    addressLine2: orEmpty(contact.addressLine2),
    city: orEmpty(contact.city),
    state: orEmpty(contact.state),
    zipCode: orEmpty(contact.zipCode),
    notes: orEmpty(contact.notes),
  };
}

/**
 * Every key is always present. The mutations read a missing key as "keep what is on the record", so
 * an omitted key on edit would strand a value the advisor just cleared.
 *
 * The entity fields clear with `''` rather than `null` because `UpdateFamilyMember` merges them into
 * relation metadata through a `.compact`, which drops nils. The two entity dates are the exception:
 * `AddFamilyMemberInput` types them as ISO8601Date, so `''` fails coercion on the add path — they
 * can be set and replaced but not blanked.
 */
export function toContactMutationInput(values: HouseholdContactFormValues) {
  const isEntity = isEntityForm(values);
  const target = values.isDependent
    ? decodeDependentTarget(values.dependentOf)
    : { dependentOfHouseholdId: null, dependentOfUserId: null };

  return {
    // The mutation copies an entity's name onto the contact's first name; there is no last name.
    firstName: isEntity ? values.entityName : values.firstName,
    middleName: isEntity ? '' : values.middleName,
    lastName: isEntity ? '' : values.lastName,
    relation: values.relation,
    isBeneficiary: values.isBeneficiary,
    isDependent: values.isDependent,
    isTrustedContact: values.isTrustedContact,
    dependentOfHouseholdId: target.dependentOfHouseholdId,
    dependentOfUserId: target.dependentOfUserId,
    dateOfBirth: isEntity ? null : emptyToNull(values.dateOfBirth),
    ssn: isEntity ? '' : values.ssn,
    email: values.email,
    phoneNumber: values.phoneNumber,
    addressLine1: values.addressLine1,
    addressLine2: values.addressLine2,
    city: values.city,
    state: values.state,
    zipCode: values.zipCode,
    notes: values.notes,
    entityType: isEntity ? values.relation : '',
    entityName: isEntity ? values.entityName : '',
    entityEin: isEntity ? values.entityEin : '',
    entityDateEstablished: isEntity ? emptyToNull(values.entityDateEstablished) : null,
    entityDateOfDeath: isEntity ? emptyToNull(values.entityDateOfDeath) : null,
    entityTrustType: isEntity ? values.entityTrustType : '',
    entityOrganizationType: isEntity ? values.entityOrganizationType : '',
    entityTrusteeName: isEntity ? values.entityTrusteeName : '',
    entityPrimaryContactRole: isEntity ? values.entityPrimaryContactRole : '',
    entityExecutorName: isEntity ? values.entityExecutorName : '',
  };
}
